import { call, put, select, takeLatest } from 'redux-saga/effects'
import * as Sentry from '@sentry/react'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import getAPIConfig from '@/shared/tools/getAPIConfig'

import {
  updateSuppliersManagementCountrySites,
  updateSuppliersManagementCreateStatuses,
  updateSuppliersManagementError,
  updateSuppliersManagementIsLoading,
  updateSuppliersManagementPagination,
  updateSuppliersManagementSuppliers,
} from './SuppliersManagementSlice'
import { selectSuppliersManagementFilters } from './SuppliersManagementSelectors'
import { SupplierApiResponse, SupplierFilters } from './types'

export const suppliersManagementActions = {
  LOAD_LIST: 'LOAD_SUPPLIERS_LIST',
  CREATE_SUPPLIER: 'CREATE_SUPPLIER',
  DEACTIVATE_SUPPLIER: 'DEACTIVATE_SUPPLIER',
  ACTIVATE_SUPPLIER: 'ACTIVATE_SUPPLIER',
}

interface AuthData {
  authTokenSession?: string
  lang?: string
  extraRoutes?: {
    suppliers?: {
      list?: string
      create?: string
      deactivate?: string
      activate?: string
    }
  }
}

const buildUrl = (route: string, lang: string | undefined, params: Record<string, string> = {}): string => {
  if (!route) return ''
  const query = new URLSearchParams({ lang: lang ?? 'fr', ...params })
  return route.includes('?') ? `${route}&${query}` : `${route}?${query}`
}

const listParams = (filters: SupplierFilters): Record<string, string> => {
  const params: Record<string, string> = { page: String(filters.page) }
  if (filters.siteId) params.site_id = filters.siteId
  if (filters.type) params.type = filters.type
  if (filters.search) params.search = filters.search
  if (filters.includeInactive) params.include_inactive = '1'
  return params
}

const doFetch = (url: string, config: any): Promise<Response> => fetch(url, config)

export function* loadSuppliersSaga(): Generator {
  try {
    const { authTokenSession, lang, extraRoutes } = (yield select(selectAuthData)) as AuthData
    const listRoute = extraRoutes?.suppliers?.list
    if (!listRoute) return

    const filters = (yield select(selectSuppliersManagementFilters)) as SupplierFilters

    yield put(updateSuppliersManagementIsLoading(true))
    yield put(updateSuppliersManagementError(''))

    const config = getAPIConfig({ method: 'get', authTokenSession, body: undefined })
    const response = (yield doFetch(buildUrl(listRoute, lang, listParams(filters)), config)) as Response
    const json = (yield call([response, response.json])) as SupplierApiResponse

    if (response.status !== 200 || json?.success === false) {
      yield put(updateSuppliersManagementError(json?.message ?? 'error'))
      yield put(updateSuppliersManagementSuppliers([]))
      return
    }

    yield put(updateSuppliersManagementSuppliers(Array.isArray(json?.data) ? json.data : []))
    yield put(updateSuppliersManagementCountrySites(json?.meta?.country_sites ?? []))
    yield put(
      updateSuppliersManagementPagination({
        lastPage: json?.meta?.last_page ?? 1,
        total: json?.meta?.total ?? 0,
      }),
    )
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { context: 'loadSuppliersSaga' } })
    }
    yield put(updateSuppliersManagementError(error instanceof Error ? error.message : 'error'))
  } finally {
    yield put(updateSuppliersManagementIsLoading(false))
  }
}

export function* createSupplier(action: any): Generator {
  const { supplierData, setFormErrors, onSuccess } = action
  try {
    const { authTokenSession, lang, extraRoutes } = (yield select(selectAuthData)) as AuthData
    const createRoute = extraRoutes?.suppliers?.create
    if (!createRoute) return

    const config = getAPIConfig({ method: 'post', authTokenSession, body: supplierData })
    const response = (yield doFetch(buildUrl(createRoute, lang), config)) as Response
    const json = (yield call([response, response.json])) as SupplierApiResponse

    if (json?.success !== true) {
      yield call(setFormErrors, json?.errors ?? {})
      return
    }

    const data = json.data
    const statuses = !Array.isArray(data) ? data?.statuses ?? {} : {}
    yield put(updateSuppliersManagementCreateStatuses(statuses))
    yield call(setFormErrors, {})
    yield call(onSuccess)
    yield call(loadSuppliersSaga)
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { context: 'createSupplierSaga' } })
    }
    yield call(setFormErrors, { name: [error instanceof Error ? error.message : 'error'] })
  }
}

export function* toggleSupplier(action: any): Generator {
  const { supplierId, activate } = action
  try {
    const { authTokenSession, lang, extraRoutes } = (yield select(selectAuthData)) as AuthData
    const route = activate
      ? extraRoutes?.suppliers?.activate?.replace('*', String(supplierId))
      : extraRoutes?.suppliers?.deactivate?.replace('*', String(supplierId))
    if (!route) return

    const config = getAPIConfig({ method: activate ? 'put' : 'delete', authTokenSession, body: {} })
    const response = (yield doFetch(buildUrl(route, lang), config)) as Response
    const json = (yield call([response, response.json])) as SupplierApiResponse

    if (json?.success !== true) {
      yield put(updateSuppliersManagementError(json?.message ?? 'error'))
      return
    }

    yield call(loadSuppliersSaga)
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { context: 'toggleSupplierSaga' } })
    }
    yield put(updateSuppliersManagementError(error instanceof Error ? error.message : 'error'))
  }
}

export function* deactivateSupplier(action: any): Generator {
  yield call(toggleSupplier, { ...action, activate: false })
}

export function* activateSupplier(action: any): Generator {
  yield call(toggleSupplier, { ...action, activate: true })
}

export default function* suppliersManagementSagas() {
  yield takeLatest(suppliersManagementActions.LOAD_LIST, loadSuppliersSaga)
  yield takeLatest(suppliersManagementActions.CREATE_SUPPLIER, createSupplier)
  yield takeLatest(suppliersManagementActions.DEACTIVATE_SUPPLIER, deactivateSupplier)
  yield takeLatest(suppliersManagementActions.ACTIVATE_SUPPLIER, activateSupplier)
}
